import Hls, { type ErrorData } from 'hls.js';
import * as React from 'react';

// The old react-player dashboard buffers these VOD recordings well on plain
// hls.js defaults — the lever is the ENGINE (we force hls.js below, including on
// Safari, matching its forceSafariHLS), not buffer sizing. So we keep hls.js's
// default buffering (maxBufferLength 30s, maxMaxBufferLength 600s, maxBufferSize
// 60MB) and only set worker + credentials.
const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,
} as const;

const MAX_NETWORK_RETRIES = 3;
const MAX_MEDIA_RECOVERIES = 2;

const canPlayNativeHLS = (video: HTMLVideoElement): boolean =>
  video.canPlayType('application/vnd.apple.mpegurl') !== '' ||
  video.canPlayType('application/x-mpegURL') !== '';

export const useHLS = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  src: string
) => {
  const hlsRef = React.useRef<Hls | null>(null);
  const [isError, setIsError] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  // Position/playback to restore when the user retries after an error.
  const resumeAtRef = React.useRef(0);
  const wasPlayingRef = React.useRef(false);

  const retry = React.useCallback(() => {
    const video = videoRef.current;
    if (video) {
      resumeAtRef.current = video.currentTime;
      wasPlayingRef.current = !video.paused && !video.ended;
    }
    setIsError(false);
    setReloadKey(key => key + 1);
  }, [videoRef]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    setIsError(false);

    // iOS compatibility: set webkit-playsinline for older iOS versions
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('playsinline', 'true');

    const isHLS =
      src.includes('.m3u8') || src.includes('application/x-mpegURL');

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const handleNativeError = () => setIsError(true);

    // Restore playback position/state after a retry (once the media reloads).
    const restore = () => {
      if (resumeAtRef.current > 0) {
        try {
          video.currentTime = resumeAtRef.current;
        } catch {
          /* seeking may not be ready yet; ignore */
        }
      }
      if (wasPlayingRef.current) void video.play();
      resumeAtRef.current = 0;
      wasPlayingRef.current = false;
      video.removeEventListener('loadeddata', restore);
    };
    if (resumeAtRef.current > 0 || wasPlayingRef.current) {
      video.addEventListener('loadeddata', restore);
    }

    const attachNative = () => {
      video.src = src;
      video.load();
      video.addEventListener('error', handleNativeError);
    };

    let networkRetries = 0;
    let mediaRecoveries = 0;

    // Capped, backed-off recovery instead of the old infinite retry loop.
    const handleFatalHlsError = (hls: Hls, data: ErrorData) => {
      if (!data.fatal) return;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        if (networkRetries < MAX_NETWORK_RETRIES) {
          networkRetries += 1;
          const delay = 2 ** (networkRetries - 1) * 1000; // 1s, 2s, 4s
          retryTimer = setTimeout(() => hls.startLoad(), delay);
        } else {
          setIsError(true);
        }
      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        if (mediaRecoveries < MAX_MEDIA_RECOVERIES) {
          mediaRecoveries += 1;
          // Second attempt: swap audio codec per hls.js recovery guidance.
          if (mediaRecoveries === 2) hls.swapAudioCodec();
          hls.recoverMediaError();
        } else {
          setIsError(true);
        }
      } else {
        setIsError(true);
      }
    };

    const attachHls = () => {
      const hls = new Hls({
        ...HLS_CONFIG,
        xhrSetup: xhr => {
          xhr.withCredentials = true;
        },
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) =>
        handleFatalHlsError(hls, data)
      );
    };

    // Force hls.js whenever it's supported — including Safari/iOS — matching the
    // old react-player dashboard's forceSafariHLS. hls.js buffers far ahead
    // where native Safari HLS loads ~one segment at a time, and its XHR shares
    // the page cookie jar so credentialed segment fetches work in webviews too.
    // Native playback is only a last-resort fallback where MSE is unavailable.
    if (!isHLS) {
      attachNative();
    } else if (Hls.isSupported()) {
      attachHls();
    } else if (canPlayNativeHLS(video)) {
      attachNative();
    } else {
      setIsError(true);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      video.removeEventListener('error', handleNativeError);
      video.removeEventListener('loadeddata', restore);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, videoRef, reloadKey]);

  return { isError, retry };
};
